import { useSelector } from 'react-redux';
import { FC, PropsWithChildren, useMemo } from 'react';
import {
    useFeatureFlag,
    useAdaChatBot,
    useGainsightService,
    AdaChatbotContext,
    ADA_CHAT_BOT_HANDLE,
} from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import { GeneralSubscription, useAppConfiguration } from '@mews-commander/data-access';
import { PlatformEnvironment } from '@mews/enums';
import { getUrlParameter } from '@mews-commander/utils';
import {
    getCurrentProfileIdOrNull,
    useCurrentEnterpriseId,
    useLanguageCode,
    useCurrentUser,
} from '@mews-commander/core';
import { getCurrentEnterpriseSubscription } from '@mews-commander/legacy-entities/src/selectors/session/subscription';

const OPEN_ADA_CHATBOT_URL_PARAM = 'openAdaChatbot';

export const GAINSIGHT_ADA_SWITCH_FUNCTION = 'switchbot';

declare global {
    interface Window {
        [GAINSIGHT_ADA_SWITCH_FUNCTION]: () => void;
    }
}

export const AdaChatBotProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
    const isAdaEmbeddedFeatureFlagEnabled = useFeatureFlag(FeatureFlag.adaEmbeddedFeatureFlagEnabled);
    const isAdaChatBotV2Enabled = useFeatureFlag(FeatureFlag.adaChatBotV2Enabled);
    const gainsightService = useGainsightService();
    const enterpriseId = useCurrentEnterpriseId();
    const { supportTier, crmIdentifier }: GeneralSubscription = useSelector(getCurrentEnterpriseSubscription);
    const { id, email, firstName } = useCurrentUser();
    const language = useLanguageCode();
    const isLoggedIn = !!getCurrentProfileIdOrNull();
    const isOpenAdaChatbotUrlParam = getUrlParameter(OPEN_ADA_CHATBOT_URL_PARAM) !== undefined;
    const { environment } = useAppConfiguration();
    const isProduction = environment === PlatformEnvironment.Production;

    const { start, stop, toggle, isStarted, isChatOpen, isChatButtonHidden, hideChatButton, showChatButton } =
        useAdaChatBot({
            enabled: isAdaEmbeddedFeatureFlagEnabled && isLoggedIn,
            startOnLoad: (isAdaEmbeddedFeatureFlagEnabled && !isAdaChatBotV2Enabled) || isOpenAdaChatbotUrlParam,
            isAdaChatBotV2Enabled,
            options: {
                handle: ADA_CHAT_BOT_HANDLE,
                language,
                cluster: 'eu',
                align: 'left',
                testMode: !isProduction,
                metaFields: {
                    enterpriseId,
                    tier: supportTier,
                    crmIdentifier,
                    userId: id,
                    email,
                    name: firstName,
                },
            },
        });

    window[GAINSIGHT_ADA_SWITCH_FUNCTION] = async () => {
        if (!isStarted) {
            await start();
        } else if (!isChatOpen || isChatButtonHidden) {
            showChatButton();
            toggle();
        }
        gainsightService?.closeKnowledgeCenter();
    };

    const adaChatBotService = useMemo(
        () => ({
            start,
            stop,
            toggle,
            isStarted,
            isChatOpen,
            isChatButtonHidden,
            hideChatButton,
            showChatButton,
        }),
        [start, stop, toggle, isStarted, isChatOpen, isChatButtonHidden, hideChatButton, showChatButton]
    );

    return <AdaChatbotContext.Provider value={adaChatBotService}>{children}</AdaChatbotContext.Provider>;
};
